import type { TokenApiDAO } from "@/domains/tokens/types"
import { LcAddress } from "@/domains/core"
import { TokenStore } from "@/domains/tokens/token-store"

/**
 * Build a placeholder `TokenStore` from a thick-v1 pool's embedded token
 * metadata. Used by `ManualClOrchestrator` to materialise tokens that the
 * thick-v1 pools query references but the global `token-api` query has not
 * yet hydrated into the `TokenRepository`.
 *
 * Why this exists:
 *   On a cold page load the token-API query and the Manual-CL pools query
 *   race. Before this factory existed, any pool whose tokens had not yet
 *   landed in the repository was silently dropped by the converter —
 *   which showed up on beta as "only one pair loads, and not even with
 *   the right data." Any pool touching an exotic token (ZRO, GURU,
 *   MORPHO, DEGEN, AERO, EURC, …) never made it onto the page, because
 *   those tokens only exist in the repo after the token-API call
 *   resolves. Local dev avoided the bug because the token repo was warm
 *   from an earlier session's cache.
 *
 *   Materialising tokens in place closes the race: every pool returned
 *   by the indexer converts successfully, regardless of whether the
 *   token-API has landed yet. Later, when the token orchestrator's
 *   `onLoadTokenQuerySuccess` runs, it finds the existing entry and
 *   calls `updateStaticData(...)`, so the placeholder is enriched with
 *   price / tags / whitelisting information without losing identity —
 *   MobX observers on the pool and position rows re-render through the
 *   same underlying `TokenStore` instance.
 */
export class ThickV1ApiFactory {
  static createToken(tokenData: {
    address: string
    symbol: string
    name: string
    decimals: number
  }): TokenStore {
    const tokenDAO: TokenApiDAO = {
      address: new LcAddress(tokenData.address),
      symbol: tokenData.symbol,
      // Fall back to the symbol for display when the pool response does not
      // carry a verbose `name`.
      name: tokenData.name || tokenData.symbol,
      decimals: tokenData.decimals,
      // No USD price is available on the pool payload (the indexer only
      // exposes `token0Price`/`token1Price`, which are intra-pair ratios).
      // Leaving this as "0" keeps the existing `updateApiPrice` semantics
      // intact — the token-API orchestrator will upsert the real price via
      // `updateStaticData` as soon as its query resolves.
      priceUsd: "0",
      isWhitelisted: false,
      isSwapToken: false,
      isIncentiveToken: false,
      tags: [],
    }

    return new TokenStore(tokenDAO, "system", "thick-v1")
  }
}
